import { createTheme, responsiveFontSizes, ThemeOptions } from '@mui/material/styles';

// ── Cosmic Indigo – Dark ────────────────────────────────────────────────────
const dark = {
  bgBase:        '#030712',
  bgSurface:     '#0D1117',
  bgElevated:    '#111827',
  bgSubtle:      '#1F2937',
  border:        '#1E293B',
  borderMuted:   '#374151',
  accent:        '#818CF8',   // indigo-400
  accentDim:     '#6366F1',   // indigo-500
  accentDeep:    '#4F46E5',   // indigo-600
  textPrimary:   '#F1F5F9',
  textSecondary: '#94A3B8',
  textTertiary:  '#64748B',
};

// ── Cosmic Indigo – Light ───────────────────────────────────────────────────
const light = {
  bgBase:        '#FFFFFF',
  bgSurface:     '#F8FAFC',
  bgElevated:    '#FFFFFF',
  bgSubtle:      '#F1F5F9',
  border:        '#E2E8F0',
  borderMuted:   '#CBD5E1',
  accent:        '#6366F1',   // indigo-500 (deeper for light-mode contrast)
  accentDim:     '#4F46E5',   // indigo-600
  accentDeep:    '#3730A3',   // indigo-800
  textPrimary:   '#0F172A',
  textSecondary: '#475569',
  textTertiary:  '#94A3B8',
};

export const createAppTheme = (mode: 'light' | 'dark') => {
  const c = mode === 'dark' ? dark : light;
  const isDark = mode === 'dark';

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: c.accent,
        dark: c.accentDim,
      },
      secondary: {
        main: c.textSecondary,
      },
      background: {
        default: c.bgBase,
        paper:   c.bgSurface,
      },
      text: {
        primary:   c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.border,
    },

    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontFamily:    '"Space Grotesk", "Inter", sans-serif',
        fontWeight:    700,
        letterSpacing: '-0.05em',
        lineHeight:    1.0,
        fontSize:      'clamp(3rem, 6vw, 5.5rem)',
      },
      h2: {
        fontFamily:    '"Space Grotesk", "Inter", sans-serif',
        fontWeight:    700,
        letterSpacing: '-0.03em',
        lineHeight:    1.15,
        fontSize:      'clamp(2rem, 4vw, 3.25rem)',
      },
      h3: {
        fontFamily:    '"Space Grotesk", "Inter", sans-serif',
        fontWeight:    600,
        letterSpacing: '-0.02em',
        lineHeight:    1.25,
        fontSize:      'clamp(1.5rem, 3vw, 2.25rem)',
      },
      h4: {
        fontFamily:    '"Space Grotesk", "Inter", sans-serif',
        fontWeight:    600,
        letterSpacing: '-0.01em',
        lineHeight:    1.3,
      },
      h5: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontWeight: 600,
        lineHeight: 1.35,
        fontSize:   '1.25rem',
      },
      h6: {
        fontFamily: '"Space Grotesk", "Inter", sans-serif',
        fontWeight: 600,
        lineHeight: 1.4,
        fontSize:   '1.0625rem',
      },
      body1: {
        fontSize:   '1.0625rem',   // 17px — more readable, Adil-style
        lineHeight: 1.8,
      },
      body2: {
        fontSize:   '0.9375rem',   // 15px — larger card text, easy to read
        lineHeight: 1.75,
      },
      overline: {
        fontWeight:    500,
        fontSize:      '0.6875rem',
        letterSpacing: '0.12em',
      },
      button: {
        textTransform: 'none',
        fontWeight:    600,
        fontFamily:    '"Space Grotesk", "Inter", sans-serif',
        fontSize:      '0.9375rem',
      },
      caption: {
        fontSize:      '0.8125rem',  // slightly larger captions
        letterSpacing: '0.02em',
        lineHeight:    1.6,
      },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*':    { boxSizing: 'border-box' },
          html:   { scrollBehavior: 'smooth' },
          ':root': {
            '--gradient-primary': `linear-gradient(135deg, ${c.accent} 0%, #a78bfa 50%, #34d399 100%)`,
            '--gradient-border':  `linear-gradient(135deg, ${c.accent}, #a78bfa)`,
            '--glow-primary':     `0 0 40px ${c.accent}30, 0 0 80px ${c.accent}10`,
            '--glow-strong':      `0 0 60px ${c.accent}50`,
            '--shadow-card':      `0 8px 32px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05)`,
            '--shadow-elevated':  `0 24px 80px rgba(0,0,0,0.6)`,
            '--blur-glass':       'blur(24px) saturate(180%)',
          },
          '*::-webkit-scrollbar':       { width: '6px' },
          '*::-webkit-scrollbar-track': { background: 'transparent' },
          '*::-webkit-scrollbar-thumb': {
            background:   c.border,
            borderRadius: '3px',
          },
          '*::-webkit-scrollbar-thumb:hover': { background: c.accent },
          '::selection': {
            background: `${c.accent}33`,
            color:      c.textPrimary,
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            padding:      '11px 28px',
            fontWeight:   600,
            fontSize:     '0.9375rem',
            transition:   'all 220ms cubic-bezier(0.4,0,0.2,1)',
          },
          contained: {
            backgroundColor: c.accent,
            color:           isDark ? '#030712' : '#ffffff',
            backgroundImage: 'none',
            boxShadow:       `0 0 24px ${c.accent}40`,
            '&:hover': {
              backgroundColor: c.accentDim,
              boxShadow:       `0 0 36px ${c.accent}60`,
              transform:       'translateY(-2px)',
            },
          },
          outlined: {
            borderColor:  c.border,
            color:        c.textPrimary,
            borderWidth:  '1.5px',
            '&:hover': {
              borderColor:     c.accent,
              borderWidth:     '1.5px',
              backgroundColor: `${c.accent}0D`,
              color:           c.accent,
              transform:       'translateY(-2px)',
            },
          },
          text: {
            color: c.accent,
            '&:hover': { backgroundColor: `${c.accent}0D` },
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgSurface,
            backgroundImage: 'none',
            border:          `1px solid ${c.border}`,
            borderRadius:    '16px',
            boxShadow:       'none',
            transition:      'border-color 250ms ease, box-shadow 250ms ease, transform 220ms ease',
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgSurface,
            backgroundImage: 'none',
            border:          `1px solid ${c.border}`,
            borderRadius:    '16px',
            boxShadow:       'none',
          },
        },
      },

      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor:       isDark ? 'rgba(3,7,18,0.65)' : 'rgba(255,255,255,0.75)',
            backdropFilter:        'blur(24px) saturate(180%)',
            WebkitBackdropFilter:  'blur(24px) saturate(180%)',
            backgroundImage:       'none',
            boxShadow:             'none',
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: c.border,
              borderWidth: '1.5px',
              transition:  'border-color 200ms ease',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: c.borderMuted,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.accent,
              borderWidth: '1.5px',
            },
            '& input':    { color: c.textPrimary },
            '& textarea': { color: c.textPrimary },
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            color:         c.textSecondary,
            '&.Mui-focused': { color: c.accent },
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgSurface,
            backgroundImage: 'none',
            borderRight:     `1px solid ${c.border}`,
          },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.border },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgElevated,
            borderColor:     c.border,
            color:           c.textSecondary,
            fontSize:        '0.8125rem',  // larger chip text
            height:          '30px',       // taller chips
            fontFamily:      '"JetBrains Mono", monospace',
            borderRadius:    '8px',
            transition:      'border-color 200ms ease, color 200ms ease, background-color 200ms ease',
            '&:hover': {
              backgroundColor: c.bgSubtle,
              borderColor:     c.accent,
              color:           c.accent,
            },
          },
          label: { paddingLeft: '12px', paddingRight: '12px' },
        },
      },

      MuiContainer: {
        styleOverrides: {
          root: { paddingTop: 0, paddingBottom: 0 },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            transition:   'color 200ms ease, background-color 200ms ease',
          },
        },
      },
    },
  };

  let theme = createTheme(themeOptions);
  theme = responsiveFontSizes(theme);
  return theme;
};
